import axios, { AxiosInstance } from 'axios';
import { authService } from './authService';
import { apiConfig } from '../config/apiConfig';
import type {
  SavedDashboard,
  DashboardWidget,
  BuilderQueryRequest,
  BuilderQueryResponse,
  BuilderChatMessage,
} from '../types/dashboard.types';

interface DashboardSummary {
  id: string;
  name: string;
  datasetIds: string[];
  widgetCount: number;
  createdAt: string;
  updatedAt: string;
}

class DashboardBuilderService {
  private ax: AxiosInstance;

  constructor() {
    this.ax = axios.create({
      baseURL: apiConfig.baseUrl,
      timeout: 60000, // 60s for AI queries
      headers: { 'Content-Type': 'application/json' },
    });

    this.ax.interceptors.request.use(async (config) => {
      const base = (config.baseURL || '').toString();
      if (base.includes('localhost:3001')) return config;
      try {
        const token = await authService.getAccessToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch {
        // allow request to fail naturally
      }
      return config;
    });
  }

  async listDashboards(): Promise<DashboardSummary[]> {
    const res = await this.ax.get<DashboardSummary[]>('/api/dashboard-builder/list');
    return res.data;
  }

  async getDashboard(id: string): Promise<SavedDashboard> {
    const res = await this.ax.get<SavedDashboard>(`/api/dashboard-builder/${id}`);
    return res.data;
  }

  async createDashboard(name: string, datasetIds: string[]): Promise<SavedDashboard> {
    const res = await this.ax.post<SavedDashboard>('/api/dashboard-builder', { name, datasetIds });
    return res.data;
  }

  async updateDashboard(
    id: string,
    updates: { name?: string; widgets?: DashboardWidget[]; chatHistory?: BuilderChatMessage[] }
  ): Promise<SavedDashboard> {
    const res = await this.ax.put<SavedDashboard>(`/api/dashboard-builder/${id}`, updates);
    return res.data;
  }

  async deleteDashboard(id: string): Promise<void> {
    await this.ax.delete(`/api/dashboard-builder/${id}`);
  }

  async query(request: BuilderQueryRequest): Promise<BuilderQueryResponse> {
    const res = await this.ax.post<BuilderQueryResponse>('/api/dashboard-builder/query', request);
    return res.data;
  }

  async getFilterValues(sql: string, datasetIds?: string[]): Promise<string[]> {
    const res = await this.ax.post<{ values: string[] }>('/api/dashboard-builder/filter-values', { sql, datasetIds });
    return res.data.values;
  }

  async filterQuery(widgetSql: string, filters: { field: string; value: string }[], datasetIds?: string[]): Promise<any> {
    const res = await this.ax.post('/api/dashboard-builder/filter-query', { widgetSql, filters, datasetIds });
    return res.data;
  }

  async getTemplate(name: string): Promise<{ widgets: any[]; filters: any[] }> {
    const res = await this.ax.get(`/api/dashboard-builder/templates/${name}`);
    return res.data;
  }
}

export const dashboardBuilderService = new DashboardBuilderService();
